/**
 * Shared scheduling lead-time lockout helpers.
 *
 * Implements client-side business days stepping and earliest schedulable date calculation,
 * matching public.add_business_days and public.get_earliest_schedulable_date in PostgreSQL.
 */

import { getJobTimezone } from './timezone';
import { parseLocalDate } from '../utils/dateHandling';

/**
 * Adds business days (Monday-Friday) to a given date, skipping Saturday and Sunday.
 *
 * Matches PostgreSQL public.add_business_days(p_from date, p_days integer).
 * If days <= 0, returns the input date (as a new Date instance).
 *
 * Days above 365 throw, mirroring the SQL function's ERRCODE 22003 rather than
 * silently clamping: a caller asking for 400 would otherwise get the date for 365
 * from the client and an exception from the database for the same input.
 * Unreachable in normal use — the stored values are constrained to 0..90.
 */
export function addBusinessDays(from: Date, days: number): Date {
    const result = new Date(from.getTime());
    if (!Number.isFinite(days) || days <= 0) {
        return result;
    }

    if (days > 365) {
        throw new RangeError('days exceeds maximum allowed business days (365)');
    }

    let added = 0;
    const targetDays = Math.floor(days);

    while (added < targetDays) {
        result.setDate(result.getDate() + 1);
        const dayOfWeek = result.getDay();
        // 0 = Sunday, 6 = Saturday
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            added++;
        }
    }

    return result;
}

export interface CalculateEarliestSchedulableDateOptions {
    leadTimeBusinessDays: number | null | undefined;
    siteState?: string | null;
    now?: Date;
}

/**
 * Resolves "today" at midnight in the job's local timezone.
 */
export function getTodayInTimezone(siteState?: string | null, now?: Date): Date {
    const targetTz = getJobTimezone({ site_state: siteState });
    const referenceDate = now ?? new Date();

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: targetTz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(referenceDate);
    const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const year = parseInt(partMap.year, 10);
    const month = parseInt(partMap.month, 10) - 1;
    const day = parseInt(partMap.day, 10);

    return new Date(year, month, day, 0, 0, 0, 0);
}

/**
 * Returns today's date formatted as YYYY-MM-DD in the job's local timezone.
 */
export function getTodayDateString(siteState?: string | null, now?: Date): string {
    const targetTz = getJobTimezone({ site_state: siteState });
    const referenceDate = now ?? new Date();

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: targetTz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
    const parts = formatter.formatToParts(referenceDate);
    const partMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return `${partMap.year}-${partMap.month}-${partMap.day}`;
}

/**
 * Calculates the earliest schedulable date given a lead-time buffer in business days.
 *
 * Resolves "today" in the job's local timezone (via getJobTimezone), anchors to midnight,
 * and steps forward by `leadTimeBusinessDays` business days.
 * Returns null if leadTimeBusinessDays is null, undefined, or <= 0 (meaning no restriction).
 */
export function calculateEarliestSchedulableDate(opts: CalculateEarliestSchedulableDateOptions): Date | null {
    const { leadTimeBusinessDays, siteState, now } = opts;
    if (leadTimeBusinessDays == null || !Number.isFinite(leadTimeBusinessDays) || leadTimeBusinessDays <= 0) {
        return null;
    }

    const todayInTz = getTodayInTimezone(siteState, now);
    return addBusinessDays(todayInTz, Math.floor(leadTimeBusinessDays));
}

/**
 * Calculates the number of lead business days between today and earliestDateStr in the job's timezone.
 * Returns 0 if earliestDateStr is on/before today or does not match within 90 business days.
 */
export function calculateLeadBusinessDays(
    earliestDateStr: string,
    siteState?: string | null,
    now?: Date,
): number {
    const todayInTz = getTodayInTimezone(siteState, now);
    const todayStr = getTodayDateString(siteState, now);
    if (earliestDateStr <= todayStr) {
        return 0;
    }
    for (let n = 1; n <= 90; n++) {
        const d = addBusinessDays(todayInTz, n);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        if (`${yyyy}-${mm}-${dd}` === earliestDateStr) {
            return n;
        }
    }
    return 0;
}

export interface FetchPostingEarliestDateOptions {
    customerId: string;
    specificJobTypeId?: number | bigint | null;
    siteState?: string | null;
    stockSupplied?: boolean;
}

/**
 * Fetches the earliest schedulable date for posting a job from public.get_posting_earliest_schedulable_date.
 * Returns null if no lead time applies or on error (fail open).
 */
export async function fetchPostingEarliestDate(
    supabase: LeadTimeRpcClient,
    opts: FetchPostingEarliestDateOptions,
): Promise<string | null> {
    const { customerId, specificJobTypeId, siteState, stockSupplied } = opts;
    if (!customerId) return null;
    try {
        const { data, error } = await supabase.rpc('get_posting_earliest_schedulable_date', {
            p_customer_id: customerId,
            p_specific_job_type_id: specificJobTypeId ?? null,
            p_site_state: siteState ?? null,
            p_stock_supplied: Boolean(stockSupplied),
        });
        if (error || typeof data !== 'string' || !data) return null;
        return data;
    } catch {
        return null;
    }
}

/**
 * The slice of a Supabase client this module needs.
 *
 * Declared with method syntax deliberately: TypeScript treats method parameters
 * bivariantly, so both the web and mobile clients (whose `rpc` narrows `fn` to a
 * union of known function names) remain assignable without an `any`.
 */
export interface LeadTimeRpcClient {
    rpc(
        fn: string,
        args: Record<string, unknown>,
    ): PromiseLike<{ data: unknown; error: unknown }>;
}

/**
 * Fetches the earliest schedulable date for a job from the database RPC.
 *
 * Calls public.get_earliest_schedulable_date(p_job_id), which applies customer default
 * and specific job type lead-time overrides for retailer stock supplied jobs.
 * Returns null if there is no restriction.
 */
export async function fetchEarliestSchedulableDate(
    supabase: LeadTimeRpcClient,
    jobId: string,
): Promise<Date | null> {
    try {
        const { data, error } = await supabase.rpc('get_earliest_schedulable_date', {
            p_job_id: jobId,
        });
        if (error || typeof data !== 'string' || !data) return null;
        const parsed = parseLocalDate(data);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
        return null;
    }
}
